import { CopyOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Card, Divider, Space, Typography, message } from 'antd'
import { isRouteErrorResponse, useRouteError } from 'react-router'

import { useGetGroupQuery } from '@web/entities/group/api/equipment-group.api'
import { useGetMachineQuery } from '@web/entities/machine/api/machines.api'
import { useGetMeasurementQuery } from '@web/entities/measurement/api/measurements.api'
import { useGetMeasuringPointQuery } from '@web/entities/point/api/point.api'
import type { TId } from '@web/shared/types/id.type'
import { useAppSelector } from '@web/shared/utils/use-app-selector.util'
import { KEY_ROOT } from '@web/widgets/equipment/equipment-tree/lib/constants'

import style from './error-page.module.css'

const { Title, Text, Paragraph } = Typography

const formatEntityLabel = (id: TId, name?: string | null): string => {
  if (!id) return '—'
  if (!name) return id

  return `${name} (${id})`
}

export const ErrorPage = () => {
  const error = useRouteError()
  const {
    selectedMeasurementId,
    selectedPointId,
    selectedMachineId,
    selectedGroupId
  } = useAppSelector((state) => state.equipment)
  const version = useAppSelector((state) => state.serverSettings.version)
  const { data: group } = useGetGroupQuery(
    { groupId: selectedGroupId },
    { skip: !selectedGroupId || selectedGroupId === KEY_ROOT }
  )
  const { data: machine } = useGetMachineQuery(
    { machineId: selectedMachineId },
    { skip: !selectedMachineId }
  )
  const { data: point } = useGetMeasuringPointQuery(
    { measuringPointId: selectedPointId },
    { skip: !selectedPointId }
  )
  const { data: measurement } = useGetMeasurementQuery(
    { measurementId: selectedMeasurementId },
    { skip: !selectedMeasurementId }
  )

  console.error(error)

  const timestamp = new Date().toLocaleString()
  const userAgent = navigator.userAgent
  const url = window.location.href

  let errorInfo = {
    message: 'Неизвестная ошибка',
    name: 'Error',
    stack: '',
    status: '',
    statusText: ''
  }

  if (error instanceof Error) {
    errorInfo = {
      ...errorInfo,
      message: error.message,
      name: error.name,
      stack: error.stack ?? ''
    }
  } else if (isRouteErrorResponse(error)) {
    errorInfo = {
      ...errorInfo,
      status: String(error.status),
      statusText: error.statusText
    }
  }

  const contextInfo = {
    group: formatEntityLabel(selectedGroupId, group?.name),
    machine: formatEntityLabel(selectedMachineId, machine?.name),
    point: formatEntityLabel(selectedPointId, point?.name),
    measurement: formatEntityLabel(selectedMeasurementId, measurement?.name)
  }

  const debugInfo = `
⚠️ Ошибка приложения
URL: ${url}
Время: ${timestamp}
User-Agent: ${userAgent}
Версия приложения: ${version}
Тип: ${errorInfo.name}
Сообщение: ${errorInfo.message}
Статус: ${errorInfo.status} ${errorInfo.statusText}
Группа: ${contextInfo.group}
Машина: ${contextInfo.machine}
Точка измерения: ${contextInfo.point}
Вид измерения: ${contextInfo.measurement}

Стек:
${errorInfo.stack || '—'}
  `.trim()

  const handleCopy = async () => {
    await navigator.clipboard.writeText(debugInfo)
    message.success('Информация об ошибке скопирована')
  }

  return (
    <div className={style.wrapper}>
      <Card className={style.container}>
        <Space direction='vertical' size='large' className={style.space}>
          <Title level={3} className={style.title}>
            ⚠ Ошибка приложения!
          </Title>
          <Divider />
          <Paragraph copyable={{ text: errorInfo.message }}>
            <Text strong>Сообщение:</Text> {errorInfo.message}
          </Paragraph>
          <Paragraph>
            <Text type='secondary'>Время:</Text> {timestamp}
            <br />
            <Text type='secondary'>Страница:</Text> {url}
            <br />
            <Text type='secondary'>Версия приложения:</Text> {version}
            <br />
            {/*<Text type='secondary'>Адрес сервера:</Text> {server?.url}*/}
            <br />
            <Text type='secondary'>Группа:</Text> {contextInfo.group}
            <br />
            <Text type='secondary'>Машина:</Text> {contextInfo.machine}
            <br />
            <Text type='secondary'>Точка измерения:</Text> {contextInfo.point}
            <br />
            <Text type='secondary'>Вид измерения:</Text>{' '}
            {contextInfo.measurement}
          </Paragraph>

          <Card size='small' className={style.info}>
            <pre className={style.pre}>{debugInfo}</pre>
          </Card>

          <Space>
            <Button
              className={style.btn}
              type='primary'
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Перезагрузить
            </Button>
            <Button
              className={style.btn}
              icon={<CopyOutlined />}
              onClick={handleCopy}
            >
              Скопировать отчёт
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  )
}
